export function readStorageItem(key) {
     if (typeof key !== "string" || key === "") {
          return null
     }
     try {
          return localStorage.getItem(key)
     } catch {
          return null
     }
}

export function writeStorageItem(key, value) {
     if (typeof key !== "string" || key === "") {
          return false
     }
     if (typeof value !== "string") {
          return false
     }
     try {
          localStorage.setItem(key, value)
          return true
     } catch {
          return false
     }
}

export function removeStorageItem(key) {
     if (typeof key !== "string" || key === "") {
          return
     }
     try {
          localStorage.removeItem(key)
     } catch {
          // localStorage nicht verfügbar: Es gibt dann auch nichts zu entfernen.
     }
}

function isUnusableRaw(raw) {
     return raw == null || raw === "" || raw === "undefined" || raw === "null"
}

export function loadStoredJson(key, fallback) {
     const raw = readStorageItem(key)
     if (isUnusableRaw(raw)) {
          return fallback
     }
     try {
          const parsed = JSON.parse(raw)
          return parsed == null ? fallback : parsed
     } catch {
          return fallback
     }
}

export function saveStoredJson(key, value) {
     try {
          return writeStorageItem(key, JSON.stringify(value))
     } catch {
          return false
     }
}

export function loadStoredJsonObject(key) {
     const parsed = loadStoredJson(key, null)
     if (parsed == null || typeof parsed !== "object" || Array.isArray(parsed)) {
          return null
     }
     return parsed
}

export function loadStoredStringArray(key) {
     const parsed = loadStoredJson(key, null)
     if (!Array.isArray(parsed)) {
          return null
     }
     const strings = parsed.filter((item) => typeof item === "string" && item !== "")
     return strings.length > 0 ? strings : null
}

function knownColumnOrder(savedOrder, knownFields) {
     if (!Array.isArray(savedOrder)) {
          return []
     }
     return savedOrder.filter((field) => typeof field === "string" && knownFields.has(field))
}

function knownActiveFields(savedActive) {
     if (!Array.isArray(savedActive)) {
          return null
     }
     return savedActive.filter((field) => typeof field === "string")
}

function columnWithActive(column, activeFields) {
     const active = activeFields == null ? column.active : activeFields.includes(column.field)
     return { ...column, active }
}

export function mergeColumnLayout(defaults, savedOrder, savedActive) {
     if (!Array.isArray(defaults) || defaults.length === 0) {
          return []
     }
     const knownFields = new Set(defaults.map((column) => column.field))
     const order = knownColumnOrder(savedOrder, knownFields)
     const activeFields = knownActiveFields(savedActive)

     if (order.length === 0 && activeFields == null) {
          return defaults.map((column) => ({ ...column }))
     }

     const byField = Object.fromEntries(defaults.map((column) => [column.field, { ...column }]))
     const merged = []
     const seen = new Set()

     for (const field of order) {
          if (seen.has(field) || byField[field] == null) {
               continue
          }
          seen.add(field)
          merged.push(columnWithActive(byField[field], activeFields))
     }
     for (const column of defaults) {
          if (seen.has(column.field)) {
               continue
          }
          merged.push(columnWithActive(column, activeFields))
     }
     return merged
}

export function mergeTableFilters(emptyFilters, storedFilters) {
     if (emptyFilters == null || typeof emptyFilters !== "object") {
          return {}
     }
     const merged = { ...emptyFilters }
     if (storedFilters == null || typeof storedFilters !== "object" || Array.isArray(storedFilters)) {
          return merged
     }
     for (const key of Object.keys(emptyFilters)) {
          const entry = storedFilters[key]
          if (entry == null || typeof entry !== "object" || Array.isArray(entry)) {
               continue
          }
          const defaultMatchMode = emptyFilters[key]?.matchMode
          const matchMode = typeof entry.matchMode === "string" ? entry.matchMode : defaultMatchMode
          merged[key] = {
               value: entry.value === undefined ? null : entry.value,
               matchMode
          }
     }
     return merged
}

export function normalizeSortState(value, validFields, fallback) {
     if (value == null || typeof value !== "object" || Array.isArray(value)) {
          return fallback
     }
     if (!Array.isArray(validFields)) {
          return fallback
     }
     const { sortField, sortOrder } = value
     const fieldIsValid = sortField == null || (typeof sortField === "string" && validFields.includes(sortField))
     const orderIsValid = sortOrder === "asc" || sortOrder === "desc"
     if (!fieldIsValid || !orderIsValid) {
          return fallback
     }
     return { sortField: sortField == null ? null : sortField, sortOrder }
}

export function parsePositiveInt(raw) {
     if (typeof raw === "number" && Number.isInteger(raw) && raw > 0) {
          return raw
     }
     if (typeof raw !== "string" || raw === "") {
          return null
     }
     if (!/^\d+$/.test(raw)) {
          return null
     }
     const parsed = Number(raw)
     if (!Number.isInteger(parsed) || parsed <= 0) {
          return null
     }
     return parsed
}
